/**
 * Pure helpers behind `PlaceholderTextField`: the text value <-> DOM mapping of a field whose
 * `{{token}}`s are shown as atomic badges.
 *
 * The stored value is always the plain string. A badge is a non-editable `<span data-raw="{{x}}">`;
 * zero-width spaces around badges give the caret somewhere to stand and never count as text.
 */

export const ZWSP = '​';

/** Mustache control tags (sections, comments, partials, delimiters) stay plain text. */
const CONTROL_SIGILS = '#^/!>=&{';

export type Segment = { type: 'text'; text: string } | { type: 'token'; raw: string; name: string };

/** Cuts a template into plain text and `{{name}}` tokens; control tags and `{{{x}}}` stay text. */
export const splitTemplate = (value: string): Segment[] => {
    const segments: Segment[] = [];
    const pattern = /\{\{([^{}]*)\}\}/g;
    let last = 0;
    let match = pattern.exec(value);

    const pushText = (text: string) => {
        if (text === '') {
            return;
        }

        const previous = segments[segments.length - 1];

        if (previous && previous.type === 'text') {
            previous.text += text;
        } else {
            segments.push({ type: 'text', text });
        }
    };

    while (match) {
        const inner = match[1].trim();
        const tripleBefore = value.charAt(match.index - 1) === '{';
        const tripleAfter = value.charAt(match.index + match[0].length) === '}';

        pushText(value.slice(last, match.index));

        if (inner === '' || CONTROL_SIGILS.includes(inner.charAt(0)) || tripleBefore || tripleAfter) {
            pushText(match[0]);
        } else {
            segments.push({ type: 'token', raw: match[0], name: inner });
        }

        last = match.index + match[0].length;
        match = pattern.exec(value);
    }

    pushText(value.slice(last));

    return segments;
};

export const isBadge = (node: Node | null | undefined): node is HTMLElement =>
    !!node && node.nodeType === 1 && (node as HTMLElement).hasAttribute('data-raw');

const isTrailingBreak = (node: Node): boolean =>
    node.nodeType === 1 && (node as HTMLElement).tagName === 'BR' && (node as HTMLElement).hasAttribute('data-trailing');

const textLength = (text: string): number => text.split(ZWSP).join('').length;

/** The serialized length of one node. */
const lengthOf = (node: Node): number => {
    if (node.nodeType === 3) {
        return textLength(node.nodeValue ?? '');
    }

    if (isBadge(node)) {
        return (node.getAttribute('data-raw') ?? '').length;
    }

    if (node.nodeType !== 1) {
        return 0;
    }

    if ((node as HTMLElement).tagName === 'BR') {
        return isTrailingBreak(node) ? 0 : 1;
    }

    return Array.from(node.childNodes).reduce((sum, child) => sum + lengthOf(child), 0);
};

/** The plain string a field shows: text, badges as their raw `{{token}}`, `<br>` as a newline. */
export const serialize = (root: Node): string => {
    const visit = (node: Node): string => {
        if (node.nodeType === 3) {
            return (node.nodeValue ?? '').split(ZWSP).join('');
        }

        if (isBadge(node)) {
            return node.getAttribute('data-raw') ?? '';
        }

        if (node.nodeType !== 1) {
            return '';
        }

        if ((node as HTMLElement).tagName === 'BR') {
            return isTrailingBreak(node) ? '' : '\n';
        }

        const inner = Array.from(node.childNodes).map(visit).join('');

        // A block the browser made on Enter or paste starts a new line.
        return node !== root && (node as HTMLElement).tagName === 'DIV' ? `\n${inner}` : inner;
    };

    return visit(root);
};

/** Whether a text node still holds a complete `{{token}}` that should become a badge. */
export const hasRawToken = (root: Node): boolean =>
    Array.from(root.childNodes).some((child) => {
        if (child.nodeType !== 3) {
            return false;
        }

        const text = (child.nodeValue ?? '').split(ZWSP).join('');

        // Runs on every render and keystroke; most text nodes hold no `{{` and need no scan.
        return text.includes('{{') && splitTemplate(text).some((segment) => segment.type === 'token');
    });

/** The serialized offset of a DOM point. */
export const offsetOfPoint = (root: Node, container: Node, offset: number): number => {
    let total = 0;
    let found = false;

    const visit = (node: Node) => {
        if (found) {
            return;
        }

        if (node === container) {
            if (node.nodeType === 3) {
                total += textLength((node.nodeValue ?? '').slice(0, offset));
            } else {
                for (let index = 0; index < offset; index++) {
                    total += lengthOf(node.childNodes[index]);
                }
            }

            found = true;

            return;
        }

        if (node.nodeType === 3 || isBadge(node) || (node.nodeType === 1 && (node as HTMLElement).tagName === 'BR')) {
            total += lengthOf(node);

            return;
        }

        Array.from(node.childNodes).forEach(visit);
    };

    visit(root);

    return total;
};

/** The DOM point for a serialized offset (clamped to the end). */
export const pointOfOffset = (root: Node, offset: number): { node: Node; offset: number } => {
    let remaining = offset;
    let result: { node: Node; offset: number } | null = null;

    const visit = (node: Node) => {
        if (result) {
            return;
        }

        if (node.nodeType === 3) {
            const value = node.nodeValue ?? '';
            const length = textLength(value);

            if (remaining <= length) {
                let seen = 0;
                let index = 0;

                while (index < value.length && seen < remaining) {
                    if (value.charAt(index) !== ZWSP) {
                        seen += 1;
                    }

                    index += 1;
                }

                // A zero-width-only node is a stand-by spot: put the caret behind the character.
                result = { node, offset: length === 0 ? value.length : index };

                return;
            }

            remaining -= length;

            return;
        }

        if (isBadge(node) || (node.nodeType === 1 && (node as HTMLElement).tagName === 'BR')) {
            remaining -= lengthOf(node);

            return;
        }

        Array.from(node.childNodes).forEach(visit);
    };

    visit(root);

    return result ?? { node: root, offset: root.childNodes.length };
};

export interface BadgeLook {
    label: string;
    /** Inline SVG of the badge icon (decorative). */
    iconHtml?: string;
    /** Inline SVG of the remove (x) glyph. */
    removeHtml?: string;
    /** Accessible name of the remove button. */
    removeLabel?: string;
    kind: 'known' | 'legacy' | 'unknown';
    title: string;
}

const BADGE_CLASS: Record<BadgeLook['kind'], string> = {
    known: 'border-primary/30 bg-primary/10 text-foreground',
    legacy: 'border-border bg-muted text-muted-foreground',
    unknown: 'border-destructive/50 bg-destructive/10 text-destructive',
};

/** One badge element; `describe` gives its label. */
export const createBadge = (document_: Document, segment: { raw: string; name: string }, look: BadgeLook): HTMLElement => {
    const badge = document_.createElement('span');

    badge.setAttribute('contenteditable', 'false');
    badge.setAttribute('data-raw', segment.raw);
    badge.setAttribute('data-name', segment.name);
    badge.setAttribute('data-kind', look.kind);
    badge.setAttribute('data-label', look.label);
    badge.setAttribute('role', 'group');
    badge.setAttribute('aria-label', look.label);
    badge.setAttribute('title', look.title);
    badge.className = `mx-0.5 inline-flex group max-w-full select-none items-center whitespace-nowrap rounded-full border px-1.5 text-xs font-medium leading-5 align-baseline ${BADGE_CLASS[look.kind]}`;
    badge.setAttribute('draggable', 'true');

    if (look.iconHtml) {
        const icon = document_.createElement('span');

        icon.setAttribute('aria-hidden', 'true');
        icon.className = 'mr-1 inline-flex shrink-0 items-center';
        icon.innerHTML = look.iconHtml;
        badge.appendChild(icon);
    }

    badge.appendChild(document_.createTextNode(look.label));

    // The (x): shown on hover, on keyboard focus and always on touch screens; removes just this badge.
    if (look.removeHtml) {
        const slot = document_.createElement('span');
        const remove = document_.createElement('span');

        // The slot is inside the badge's own box and has no width at rest; it opens on hover / focus /
        // touch and closes while a drag is under way. The button keeps a 24 px hit area.
        slot.className = 'inline-flex h-5 w-0 shrink-0 items-center justify-center overflow-hidden transition-[width] duration-150 motion-reduce:transition-none group-hover:w-6 group-focus-within:w-6 [@media(pointer:coarse)]:w-6 [[data-dragging]_&]:!w-0';
        remove.setAttribute('data-remove', '');
        remove.setAttribute('role', 'button');
        remove.setAttribute('tabindex', '0');
        remove.setAttribute('aria-label', look.removeLabel ?? '');
        remove.setAttribute('draggable', 'false');
        remove.className = 'inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
        remove.innerHTML = look.removeHtml;
        slot.appendChild(remove);
        badge.appendChild(slot);
    }

    return badge;
};

/** Builds the field content for a value: text nodes and badges, zero-width spaces between. */
export const buildContent = (
    root: HTMLElement,
    value: string,
    describe: (name: string) => BadgeLook,
    multiline: boolean,
): void => {
    const document_ = root.ownerDocument;
    const segments = splitTemplate(value);

    root.textContent = '';
    root.appendChild(document_.createTextNode(ZWSP));

    segments.forEach((segment) => {
        if (segment.type === 'token') {
            root.appendChild(createBadge(document_, segment, describe(segment.name)));
            root.appendChild(document_.createTextNode(ZWSP));

            return;
        }

        segment.text.split('\n').forEach((line, index) => {
            if (index > 0) {
                root.appendChild(document_.createElement('br'));
            }

            if (line !== '') {
                root.appendChild(document_.createTextNode(line));
            }
        });
    });

    if (multiline) {
        const trailing = document_.createElement('br');

        trailing.setAttribute('data-trailing', '');
        root.appendChild(trailing);
    }
};

/** Whether every badge in the DOM still carries the label and look `describe` gives today. */
export const badgesMatch = (root: HTMLElement, describe: (name: string) => BadgeLook): boolean =>
    Array.from(root.querySelectorAll('[data-raw]')).every((badge) => {
        const look = describe(badge.getAttribute('data-name') ?? '');

        return badge.getAttribute('data-label') === look.label && badge.getAttribute('data-kind') === look.kind;
    });

/** The badge right next to a collapsed caret in one direction, skipping zero-width stand-bys. */
export const adjacentBadge = (root: HTMLElement, offset: number, direction: 'before' | 'after'): { start: number; end: number } | null => {
    let position = 0;
    let result: { start: number; end: number } | null = null;

    const visit = (node: Node) => {
        if (result) {
            return;
        }

        if (isBadge(node)) {
            const length = lengthOf(node);

            if (direction === 'before' && position + length === offset) {
                result = { start: position, end: position + length };
            } else if (direction === 'after' && position === offset) {
                result = { start: position, end: position + length };
            }

            position += length;

            return;
        }

        if (node.nodeType === 1 && (node as HTMLElement).tagName !== 'BR') {
            Array.from(node.childNodes).forEach(visit);

            return;
        }

        position += lengthOf(node);
    };

    Array.from(root.childNodes).forEach(visit);

    return result;
};


/** Moves or copies a token inside a string. `target` is an offset of the original string. */
export const moveToken = (
    value: string,
    range: { start: number; end: number },
    target: number,
    copy: boolean,
): { value: string; caret: number } => {
    const text = value.slice(range.start, range.end);

    if (copy) {
        return { value: `${value.slice(0, target)}${text}${value.slice(target)}`, caret: target + text.length };
    }

    // A drop inside the token itself changes nothing.
    if (target >= range.start && target <= range.end) {
        return { value, caret: range.end };
    }

    const without = `${value.slice(0, range.start)}${value.slice(range.end)}`;
    const adjusted = target > range.end ? target - (range.end - range.start) : target;

    return { value: `${without.slice(0, adjusted)}${text}${without.slice(adjusted)}`, caret: adjusted + text.length };
};

/** Takes a range out of a string. */
export const removeRange = (value: string, range: { start: number; end: number }): string =>
    `${value.slice(0, range.start)}${value.slice(range.end)}`;

/** The serialized range of the badge element, for a drag that starts on it. */
export const rangeOfBadge = (root: HTMLElement, badge: HTMLElement): { start: number; end: number } => {
    const parent = badge.parentNode as Node;
    const index = Array.from(parent.childNodes).indexOf(badge as ChildNode);
    const start = offsetOfPoint(root, parent, index);

    return { start, end: start + (badge.getAttribute('data-raw') ?? '').length };
};
